import React from "react";
import { MdAccessTime, MdAttachMoney, MdPerson, MdCalendarToday, MdTrendingUp, MdWarning, MdPauseCircle } from "react-icons/md";

const STATUS_CFG = {
  Ongoing: { bg: "#E8F5E9", text: "#2E7D32", dot: "#43A047", label: "Ongoing" },
  Delayed: { bg: "#FFF8E1", text: "#E65100", dot: "#FB8C00", label: "Delayed" },
  Stopped: { bg: "#FFEBEE", text: "#B71C1C", dot: "#E53935", label: "Stopped" },
};

const StatusIcon = ({ status }) => {
  if (status === "Ongoing") return <MdTrendingUp size={13} />;
  if (status === "Delayed") return <MdWarning size={13} />;
  return <MdPauseCircle size={13} />;
};

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(n || 0);

export default function ProjectCard({ project, onClick }) {
  const cfg = STATUS_CFG[project.status] || STATUS_CFG.Ongoing;
  const profitability = project.totalContractValue > 0
    ? ((project.forecastedProfit / project.totalContractValue) * 100).toFixed(1)
    : 0;
  const profitColor = project.forecastedProfit >= 0 ? "#2E7D32" : "#B71C1C";

  const initials = (name) => (name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);

  return (
    <div
      onClick={onClick}
      style={{
        background: "#fff",
        borderRadius: "14px",
        border: "1px solid #e8e8f0",
        padding: "16px",
        cursor: "pointer",
        transition: "all .2s",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(55,97,233,0.12)"; e.currentTarget.style.borderColor = "#3761E9"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)"; e.currentTarget.style.borderColor = "#e8e8f0"; }}
    >
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontWeight: 700, fontSize: "13px", color: "#1a1a2e", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.title}
          </p>
          <p style={{ fontSize: "11px", color: "#888", marginTop: "3px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.clientName}
          </p>
        </div>
        <div style={{
          display: "flex", alignItems: "center", gap: "4px",
          background: cfg.bg, color: cfg.text,
          borderRadius: "999px", padding: "3px 8px",
          fontSize: "10px", fontWeight: 700, textTransform: "uppercase",
          letterSpacing: "0.4px", flexShrink: 0, marginLeft: "8px",
        }}>
          <StatusIcon status={project.status} />
          {cfg.label}
        </div>
      </div>

      {/* Contract Value */}
      <div style={{ marginBottom: "10px" }}>
        <p style={{ fontSize: "10px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>Contract Value</p>
        <p style={{ fontSize: "18px", fontWeight: 800, color: "#1a1a2e" }}>{fmt(project.totalContractValue)}</p>
      </div>

      {/* Financials Row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginBottom: "10px" }}>
        <div style={{ background: "#f7f8ff", borderRadius: "8px", padding: "8px" }}>
          <p style={{ fontSize: "9px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.4px" }}>Labor Cost</p>
          <p style={{ fontSize: "13px", fontWeight: 700, color: "#3761E9" }}>{fmt(project.laborCost)}</p>
        </div>
        <div style={{ background: "#f7f8ff", borderRadius: "8px", padding: "8px" }}>
          <p style={{ fontSize: "9px", color: "#aaa", textTransform: "uppercase", letterSpacing: "0.4px" }}>Ext. Costs</p>
          <p style={{ fontSize: "13px", fontWeight: 700, color: "#FB8C00" }}>{fmt(project.externalCosts)}</p>
        </div>
      </div>

      {/* Forecasted Profit */}
      <div style={{ background: project.forecastedProfit >= 0 ? "#E8F5E9" : "#FFEBEE", borderRadius: "8px", padding: "8px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "10px", color: profitColor, fontWeight: 600, textTransform: "uppercase" }}>Forecasted Profit</span>
        <span style={{ fontSize: "14px", fontWeight: 800, color: profitColor }}>
          {fmt(project.forecastedProfit)} <span style={{ fontSize: "10px" }}>({profitability}%)</span>
        </span>
      </div>

      {/* Dates + Managers */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "4px", fontSize: "10px", color: "#888" }}>
          <MdCalendarToday size={12} />
          <span>{project.deliveryDate || "—"}</span>
        </div>
        <div style={{ display: "flex", gap: "4px" }}>
          {(project.assignedManagers || []).slice(0, 3).map((m, i) => (
            <div key={i} title={m.name} style={{
              width: "24px", height: "24px", borderRadius: "50%",
              background: "#3761E9", color: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "9px", fontWeight: 700, border: "2px solid #fff",
              overflow: "hidden",
            }}>
              {m.avatar
                ? <img src={m.avatar} alt={m.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                : initials(m.name)}
            </div>
          ))}
          {project.assignedManagers?.length > 3 && (
            <div style={{ width: "24px", height: "24px", borderRadius: "50%", background: "#f0f0f8", color: "#888", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "9px", fontWeight: 700 }}>
              +{project.assignedManagers.length - 3}
            </div>
          )}
        </div>
      </div>

      {/* Referral badge */}
      {project.referrerName && (
        <div style={{ marginTop: "8px", padding: "5px 8px", background: "#FFF8E1", borderRadius: "6px", fontSize: "10px", color: "#E65100" }}>
          🤝 Referral: {project.referrerName} ({project.referralRate}%)
        </div>
      )}
    </div>
  );
}
